import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from 'environments/environment';
import { Subscription, filter } from 'rxjs';
import { SKIP_ERROR_CAPTURE } from '../app-toast/http-error-capture.interceptor';

interface PendingView {
	path: string;
	at: string;
	/** Já falhou uma vez: se falhar de novo, é descartado. */
	retried?: boolean;
}

const FLUSH_EVERY_MS = 30_000;
const FLUSH_AT = 20;
/** Mesma tela de novo em menos de 3 s (redirect, reload de rota) não conta. */
const DEDUPE_MS = 3_000;
const MAX_BUFFER = 200;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
/** Telas que não são uso (login/logout/erro). */
const IGNORED = /^\/(sign-in|sign-out|signed-in-redirect|unlock-session|confirmation-required|404|500|404-not-found)?$/;

/**
 * Registra as telas abertas para o painel Management > System usage.
 *
 * A auditoria do banco só vê gravação; sem isto, quem só consulta o sistema
 * não aparece. Manda o mínimo (rota sem ids nem query, e a hora) em lote, a
 * cada 30 s ou ao esconder a aba. Nunca atrapalha a tela: sem toast, sem
 * retry insistente, e a falha não vira o "motivo" de outro erro.
 */
@Injectable({ providedIn: 'root' })
export class UsageTrackerService implements OnDestroy {
	private buffer: PendingView[] = [];
	private last: { path: string; at: number } | null = null;
	private sub?: Subscription;
	private timer?: ReturnType<typeof setInterval>;
	private sending = false;
	private readonly onHidden = () => {
		if (document.visibilityState === 'hidden') this.flush();
	};

	constructor(
		private readonly router: Router,
		private readonly http: HttpClient,
		private readonly zone: NgZone,
	) {}

	start(): void {
		if (this.sub) return;
		this.sub = this.router.events
			.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
			.subscribe((e) => this.track(e.urlAfterRedirects));
		// Fora da zona: o timer não deve disparar detecção de mudanças a cada 30 s.
		this.zone.runOutsideAngular(() => {
			this.timer = setInterval(() => this.flush(), FLUSH_EVERY_MS);
			document.addEventListener('visibilitychange', this.onHidden);
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
		if (this.timer) clearInterval(this.timer);
		document.removeEventListener('visibilitychange', this.onHidden);
	}

	private track(url: string): void {
		const path = normalize(url);
		if (IGNORED.test(path)) return;
		const now = Date.now();
		if (this.last && this.last.path === path && now - this.last.at < DEDUPE_MS) return;
		this.last = { path, at: now };
		this.buffer.push({ path, at: new Date(now).toISOString() });
		if (this.buffer.length > MAX_BUFFER) this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
		if (this.buffer.length >= FLUSH_AT) this.flush();
	}

	private flush(): void {
		if (this.sending || !this.buffer.length) return;
		const views = this.buffer.splice(0, 50);
		this.sending = true;
		this.http
			.post(`${environment.url}system-usage/page-views`, { views }, { context: new HttpContext().set(SKIP_ERROR_CAPTURE, true) })
			.subscribe({
				next: () => (this.sending = false),
				error: () => {
					this.sending = false;
					// Uma segunda chance no próximo ciclo; se cair de novo, o lote some
					// (é estatística de uso, não dado do usuário).
					if (!views.some((v) => v.retried)) {
						this.buffer.unshift(...views.map((v) => ({ ...v, retried: true })));
					}
				},
			});
	}
}

/** "/forms/<uuid>/samples?tab=1" → "/forms/:id/samples" (o servidor normaliza de novo). */
function normalize(url: string): string {
	const parts = (url || '')
		.split(/[?#]/)[0]
		.split('/')
		.filter(Boolean)
		.map((p) => (UUID.test(p) || NUMERIC.test(p) ? ':id' : p.toLowerCase()));
	return '/' + parts.join('/');
}
